import React, { useState } from 'react';
import { ChevronDown, TrendingUp, Download, ChevronRight } from 'lucide-react';
import { usePageTitle } from '../components/AppShell';
import { useLang } from '../lib/lang';
import { type AuthUser } from '../lib/auth';
import { T } from '../lib/tokens';

interface Props { user: AuthUser }

interface SubjectGrade {
  id:       string;
  name:     string;
  nameEn:   string;
  teacher:  string;
  grade:    string;
  pct:      number;
  trend:    number[]; // last 3 assessments percentage
}

interface Report {
  id:    string;
  title: string;
  term:  string;
  date:  string;
  past?: boolean;
}

const SUBJECTS: SubjectGrade[] = [
  { id:'1', name:'Bahasa Melayu',       nameEn:'Malay Language',     teacher:'Cikgu Aishah Hj Salleh',   grade:'A',  pct:88, trend:[82,85,88] },
  { id:'2', name:'Bahasa Inggeris',     nameEn:'English Language',   teacher:'Cikgu Rashid Osman',        grade:'B+', pct:76, trend:[71,74,76] },
  { id:'3', name:'Matematik',           nameEn:'Mathematics',        teacher:'Cikgu Ahmad Zulkarnain',   grade:'A',  pct:91, trend:[88,89,91] },
  { id:'4', name:'Sains',               nameEn:'Science',            teacher:'Cikgu Nurul Huda',          grade:'B',  pct:72, trend:[68,70,72] },
  { id:'5', name:'Sejarah',             nameEn:'History',            teacher:'Cikgu Hj Masri',            grade:'A-', pct:83, trend:[79,81,83] },
  { id:'6', name:'Geografi',            nameEn:'Geography',          teacher:'Cikgu Siti Noraini',        grade:'B+', pct:77, trend:[73,75,77] },
  { id:'7', name:'Pendidikan Islam',    nameEn:'Islamic Studies',    teacher:'Ustaz Hafiz Jamil',         grade:'A',  pct:92, trend:[90,91,92] },
  { id:'8', name:'Pendidikan Jasmani',  nameEn:'Physical Education', teacher:'Cikgu Azlan Hj Nordin',    grade:'A+', pct:96, trend:[94,95,96] },
];

const REPORTS: Report[] = [
  { id:'r1', title:'Laporan Kemajuan — Penggal 1 2025/2026', term:'Penggal 1', date:'15 Mac 2026' },
  { id:'r2', title:'Laporan Kemajuan — Penggal 2 2024/2025', term:'Penggal 2', date:'10 Ogos 2025', past:true },
  { id:'r3', title:'Laporan Kemajuan — Penggal 1 2024/2025', term:'Penggal 1', date:'28 Mac 2025', past:true },
];

const GRADE_COLOR: Record<string, string> = {
  'A+': T.color.success, 'A': T.color.success, 'A-': T.color.success,
  'B+': T.color.info,    'B': T.color.info,    'B-': T.color.info,
  'C+': T.color.warning, 'C': T.color.warning,
  'D':  T.color.danger,  'F': T.color.danger,
};

function Sparkline({ vals, color }: { vals: number[]; color: string }) {
  const min = 50; const max = 100;
  const h = 28; const w = 48;
  const pts = vals.map((v, i) => {
    const x = (i / (vals.length - 1)) * w;
    const y = h - ((v - min) / (max - min)) * h;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ overflow: 'visible' }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      {vals.map((v, i) => {
        const x = (i / (vals.length - 1)) * w;
        const y = h - ((v - min) / (max - min)) * h;
        return <circle key={i} cx={x} cy={y} r="2.5" fill={color} />;
      })}
    </svg>
  );
}

export function ParentAkademikPage({ user }: Props) {
  const { lang } = useLang();
  const E = lang === 'en';
  usePageTitle(E ? 'Academic' : 'Akademik');

  const [activeTab, setActiveTab] = useState<'grades' | 'reports'>('grades');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <div style={{ maxWidth: '960px' }}>
      {/* Page header */}
      <div className="page-header">
        <div>
          <h1 style={{ fontFamily: T.font.serif, fontSize: '24px', fontWeight: 700, color: T.color.textPrimary, marginBottom: '4px' }}>
            {E ? 'Academic' : 'Akademik'}
          </h1>
          <p style={{ fontSize: '13px', color: T.color.textSecondary }}>
            {E ? 'Penggal 2, Tahun Akademik 2025/2026 · Ahmad Zulfikri' : 'Penggal 2, Tahun Akademik 2025/2026 · Ahmad Zulfikri'}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '2px solid #E5E7EB', marginBottom: '24px', gap: '0' }}>
        {[
          { key: 'grades',  ms: 'Gred & Markah', en: 'Grades' },
          { key: 'reports', ms: 'Laporan Kemajuan', en: 'Progress Reports' },
        ].map(t => (
          <button
            key={t.key}
            className={`tab-item${activeTab === t.key ? ' active' : ''}`}
            onClick={() => setActiveTab(t.key as 'grades' | 'reports')}
          >
            {E ? t.en : t.ms}
          </button>
        ))}
      </div>

      {/* ── Grades tab ── */}
      {activeTab === 'grades' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {SUBJECTS.map(s => {
              const gColor = GRADE_COLOR[s.grade] ?? T.color.textSecondary;
              const expanded = expandedId === s.id;
              return (
                <div
                  key={s.id}
                  style={{
                    background: '#FFFFFF',
                    border: `1px solid ${expanded ? '#F7D000' : '#E5E7EB'}`,
                    borderRadius: '10px',
                    padding: '20px 24px',
                    cursor: 'pointer',
                    transition: 'all 150ms ease',
                    boxShadow: expanded ? '0 4px 12px rgba(247,208,0,0.12)' : '0 1px 3px rgba(0,0,0,0.06)',
                  }}
                  onClick={() => setExpandedId(expanded ? null : s.id)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: T.font.serif, fontSize: '15px', fontWeight: 600, color: T.color.textPrimary, marginBottom: '3px' }}>
                        {E ? s.nameEn : s.name}
                      </div>
                      <div style={{ fontSize: '12px', color: T.color.textSecondary }}>
                        {s.teacher}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '12px' }}>
                      <div style={{ fontFamily: T.font.serif, fontSize: '30px', fontWeight: 700, color: gColor, lineHeight: 1 }}>
                        {s.grade}
                      </div>
                      <div style={{ fontSize: '11px', color: T.color.textSecondary, marginTop: '2px' }}>
                        {s.pct}%
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '14px' }}>
                    <div>
                      <div style={{ fontSize: '10px', color: T.color.textSecondary, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {E ? 'Trend (last 3)' : 'Trend (3 terkini)'}
                      </div>
                      <Sparkline vals={s.trend} color={gColor} />
                    </div>
                    <ChevronRight size={16} color="#9CA3AF" style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 150ms' }} />
                  </div>

                  {expanded && (
                    <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #F3F4F6' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            {[E?'Assessment':'Penilaian', E?'Score':'Markah', E?'Grade':'Gred'].map(h => (
                              <th key={h} style={{ textAlign: 'left', fontSize: '10px', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', paddingBottom: '8px' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            { name: E?'Mid-term Test':'Ujian Pertengahan', score: s.trend[0], grade: s.trend[0]>=80?'A':s.trend[0]>=70?'B':'C' },
                            { name: E?'Assignment':'Tugasan',              score: s.trend[1], grade: s.trend[1]>=80?'A':s.trend[1]>=70?'B':'C' },
                            { name: E?'Final Exam':'Peperiksaan Akhir',    score: s.trend[2], grade: s.trend[2]>=80?'A':s.trend[2]>=70?'B':'C' },
                          ].map(a => (
                            <tr key={a.name}>
                              <td style={{ fontSize: '12px', color: T.color.textPrimary, paddingBottom: '6px' }}>{a.name}</td>
                              <td style={{ fontSize: '12px', color: T.color.textPrimary, paddingBottom: '6px' }}>{a.score}%</td>
                              <td style={{ paddingBottom: '6px' }}>
                                <span className="badge" style={{ background: (GRADE_COLOR[a.grade]??'#6B7280')+'22', color: GRADE_COLOR[a.grade]??'#6B7280', fontSize: '10px' }}>{a.grade}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── Reports tab ── */}
      {activeTab === 'reports' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {REPORTS.map(r => (
            <div key={r.id} style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '10px', padding: '20px 24px', display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ width: '40px', height: '40px', background: '#EFF6FF', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <TrendingUp size={20} color="#1D4ED8" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: T.color.textPrimary, marginBottom: '3px' }}>{r.title}</div>
                <div style={{ fontSize: '12px', color: T.color.textSecondary }}>
                  {E ? 'Issued:' : 'Dikeluarkan:'} {r.date}
                  {r.past && <span className="badge" style={{ background: '#F3F4F6', color: '#6B7280', marginLeft: '8px', fontSize: '10px' }}>{E?'Past year':'Tahun lepas'}</span>}
                </div>
              </div>
              <button className="btn-primary btn-sm" style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Download size={13} />
                {E ? 'Download PDF' : 'Muat Turun PDF'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
